import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Info, Point } from '../../assets/images';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import Dropdown from '../../components/dropdown';
import DataItem from '../../components/dataItem';
import { logAPI } from '../../actions/imsServer';
import { POINT_LOG_TYPES, API_PAGE_SIZE } from '../../constants';
import RewardHistoryItem from '../../components/rewardHistoryItem';

const ITEMS_PER_PAGE = API_PAGE_SIZE;

export const RewardHistory = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 필터 상태 (내부 키: 7_days, 1_month, 2_years)
    const [periodFilter, setPeriodFilter] = useState('7_days');
    const [typeFilter, setTypeFilter] = useState(-1);

    // 실제 데이터 상태
    const [pointLogs, setPointLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const offsetRef = useRef(0);
    const [error, setError] = useState(null);
    const [monthlyExpiry, setMonthlyExpiry] = useState(0);
    const scrollContainerRef = useRef(null);

    // 이달 소멸예정 포인트 계산
    const calculateMonthlyExpiry = (logs) => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth(); // 0-11

        // 2년 전 같은 달의 적립 포인트들 찾기
        const twoYearsAgo = currentYear - 2;

        let expiryPoints = 0;

        // 날짜순으로 정렬 (오래된 것부터)
        const sortedLogs = [...logs].sort((a, b) => new Date(a.time) - new Date(b.time));

        // 2년 전 같은 달에 적립된 포인트들을 추적
        const expiryCandidates = [];

        sortedLogs.forEach(log => {
            const logDate = new Date(log.time);
            const logYear = logDate.getFullYear();
            const logMonth = logDate.getMonth();

            if (log.point > 0) { // 적립 포인트
                // 2년 전 같은 달에 적립된 포인트인지 확인
                if (logYear === twoYearsAgo && logMonth === currentMonth) {
                    expiryCandidates.push({
                        amount: log.point,
                        date: logDate,
                        used: false
                    });
                }
            } else if (log.point < 0) { // 사용 포인트
                // 사용된 포인트를 선입선출로 차감
                let remainingUsage = Math.abs(log.point);

                for (let i = 0; i < expiryCandidates.length && remainingUsage > 0; i++) {
                    if (!expiryCandidates[i].used) {
                        const availableAmount = expiryCandidates[i].amount;
                        const deduction = Math.min(availableAmount, remainingUsage);

                        expiryCandidates[i].amount -= deduction;
                        remainingUsage -= deduction;

                        if (expiryCandidates[i].amount <= 0) {
                            expiryCandidates[i].used = true;
                        }
                    }
                }
            }
        });

        // 사용되지 않은 소멸예정 포인트 합산
        expiryPoints = expiryCandidates
            .filter(candidate => !candidate.used)
            .reduce((sum, candidate) => sum + candidate.amount, 0);

        return expiryPoints;
    };

    // 기간 필터에 따른 startTime 계산
    const getStartTimeFromFilter = useCallback(() => {
        const now = new Date();
        let startTime;

        switch (periodFilter) {
            case '7_days':
                startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
                break;
            case '1_month':
                startTime = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0, 0);
                break;
            case '2_years':
                startTime = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate(), 0, 0, 0, 0);
                break;
            default:
                startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
        }

        return startTime.toISOString();
    }, [periodFilter]);

    // 실제 데이터 가져오기 (무한스크롤)
    const fetchPointLogs = useCallback(async (reset = false) => {
        if (isLoadingMore && !reset) return;

        const currentOffset = reset ? 0 : offsetRef.current;

        if (!meInfo.id) {
            setLoading(false);
            return;
        }

        if (reset) {
            setLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const params = {
                userId: meInfo.id,
                limit: ITEMS_PER_PAGE,
                offset: currentOffset,
                startTime: getStartTimeFromFilter(),
            };

            // 타입 필터가 -1이 아닐 때만 type 파라미터 추가
            if (typeFilter !== -1) {
                params.type = typeFilter;
            }

            const pointLogsResponse = await logAPI.getPointLogs(params);

            if (pointLogsResponse?.data) {
                const logs = pointLogsResponse.data.logs || [];
                const total = pointLogsResponse.data.total || 0;

                if (reset) {
                    setPointLogs(logs);
                    setOffset(logs.length);
                    offsetRef.current = logs.length;

                    // 이달 소멸예정 포인트 계산 (첫 로드시만)
                    const expiryPoints = calculateMonthlyExpiry(logs);
                    setMonthlyExpiry(expiryPoints);
                } else {
                    setPointLogs(prev => [...prev, ...logs]);
                    const newOffset = currentOffset + logs.length;
                    setOffset(newOffset);
                    offsetRef.current = newOffset;
                }

                // 더 불러올 데이터가 있는지 확인
                const finalOffset = reset ? logs.length : currentOffset + logs.length;
                setHasMore(finalOffset < total && logs.length === ITEMS_PER_PAGE);
            }
        } catch (err) {
            console.error('포인트 로그 데이터 로딩 오류:', err);
            if (reset) {
                setError(err.message);
            }
        } finally {
            if (reset) {
                setLoading(false);
            }
            setIsLoadingMore(false);
        }
    }, [meInfo.id, isLoadingMore, getStartTimeFromFilter, typeFilter]);

    // 초기 로드 및 필터 변경 시 데이터 로드 (fetchPointLogs는 useCallback이지만 의존성 제외로 반복 호출 방지)
    useEffect(() => {
        if (meInfo.id) {
            setOffset(0);
            offsetRef.current = 0;
            setHasMore(true);
            fetchPointLogs(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- periodFilter/typeFilter 변경 시에만 재실행
    }, [meInfo.id, periodFilter, typeFilter]);

    // 무한 스크롤 핸들러
    const handleScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(100px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoadingMore && !loading) {
            fetchPointLogs(false);
        }
    }, [hasMore, isLoadingMore, loading, fetchPointLogs]);

    // 드롭다운 옵션 (value: 내부 키, label: 번역)
    const periodOptions = [
        { value: '7_days', label: t('last_7_days') },
        { value: '1_month', label: t('1_month') },
        { value: '2_years', label: t('last_2_years') },
    ];

    // 포인트 데이터 (실제 사용자 정보에서 가져옴)
    const pointData = {
        currentPoints: meInfo.point || 0,
        tomorrowExpected: 0, // 이 값은 별도 API에서 가져와야 함
        monthlyExpiry: monthlyExpiry, // 계산된 이달 소멸예정 포인트
    };

    const dataItemSx = {
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        rowGap: '6px',
        columnGap: '8px',
        minWidth: 0,
    };

    const dataItemValueSx = {
        color: theme.palette.figma.white[60],
        textAlign: 'left',
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '-0.42px',
        whiteSpace: 'normal',
        wordBreak: 'keep-all',
        overflowWrap: 'break-word',
        minWidth: 0,
        flex: '1 1 auto',
    };

    const dataItemUnitSx = {
        color: theme.palette.figma.white[100],
        textAlign: 'left',
        fontSize: '14px',
        fontWeight: 700,
        letterSpacing: '-0.42px',
        flexShrink: 0,
    };

    return (
        <ContentArea>
            <Stack spacing={2} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* 포인트 카드 */}
                <CommonCard
                    sx={{
                        backgroundColor: theme.palette.figma.block,
                        borderRadius: '20px',
                        padding: '10px',
                        boxShadow: theme.palette.shadows?.custom?.card || '0px 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    {/* 보유 포인트 */}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: '300px' }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Point size={20} color={'#FC0'} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: theme.palette.figma.white[60],
                                    letterSpacing: '-0.42px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'keep-all',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                {t('available_points')}
                            </Typography>
                        </Stack>
                        <Typography
                            sx={{
                                fontSize: '30px',
                                fontWeight: 600,
                                letterSpacing: '-0.9px',
                            }}
                        >
                            {pointData.currentPoints.toLocaleString()} P
                        </Typography>
                    </Stack>

                    {/* 내일 적립예정 & 이달 소멸예정 */}
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            backgroundColor: theme.palette.figma.white[10],
                            borderRadius: '10px',
                            padding: '10px',
                            position: 'relative',
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-start">
                            <Info size={20} />
                            {/* <DataItem
                                sx={dataItemSx}
                                valueSx={dataItemValueSx}
                                unitSx={dataItemUnitSx} value="내일 적립예정 :" unit={pointData.tomorrowExpected} /> */}

                            <DataItem
                                sx={dataItemSx}
                                valueSx={dataItemValueSx}
                                unitSx={dataItemUnitSx} value={t('expiring_this_month')} unit={monthlyExpiry} />
                        </Stack>
                    </Box>

                </CommonCard>

                {/* 필터 드롭다운 */}
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    sx={{
                        padding: '10px',
                        borderBottom: '1px solid ' + theme.palette.figma.white[30],
                        width: '100%',
                        minWidth: 0,
                        gap: 2,
                    }}
                >
                    <Dropdown
                        options={periodOptions}
                        value={periodFilter}
                        onChange={(option) => setPeriodFilter(option.value)}
                        sx={{ minWidth: '124px' }}
                    />
                    <Dropdown
                        options={POINT_LOG_TYPES}
                        value={typeFilter}
                        onChange={(option) => setTypeFilter(option.value)}
                        sx={{ minWidth: '124px' }}
                    />
                </Stack>

                {/* 리워드 히스토리 리스트 */}
                <Stack
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    spacing={0}
                    sx={{
                        width: '100%',
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minHeight: 0,
                        paddingBottom: '80px',
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography>{t('loading')}</Typography>
                        </Box>
                    ) : error ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography color="error">{t('error')}: {error}</Typography>
                        </Box>
                    ) : pointLogs.length > 0 ? (
                        <>
                            {pointLogs.map((item, index) => (
                                <RewardHistoryItem key={item.id || index} item={item} />
                            ))}
                            {isLoadingMore && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                                    <Typography>{t('loading')}</Typography>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <Typography>{t('no_point_history')}</Typography>
                        </Box>
                    )}
                </Stack>
            </Stack>
        </ContentArea>
    );
};

export default RewardHistory;